import type { ReactElement, ReactNode } from 'react';
import { Field, MultiCombobox, SegmentedControl, Select, type MultiComboboxOption } from '@/components/ui';
import type { FabCycle, FabRegion } from '@/lib/types';
import {
  PiFilterChips,
  PiSearch,
  PiStat,
  PiStatGrid,
  PiToolbar,
  piCount,
  piPct,
} from '../pi-kit';
import {
  FORMAT_LABEL,
  resetEventsFilters,
  type EventsFormatFilter,
  type EventsHeadline,
  type EventsUrlState,
  type ProductOption,
} from './events-model';

/**
 * The Events page's headline tiles and its one toolbar row.
 *
 * Tiles (max 4): Events, Registrations, Attendees (attendance rate as the
 * detail), Booth connections.
 * Toolbar: Cycle, Format (All / In person / Virtual), Region, Product, Search.
 * Every non-default filter is a removable chip; Reset clears them all (the tab
 * stays).
 */

export function EventsHeadlineTiles({
  headline,
  loading,
}: {
  headline: EventsHeadline | null;
  loading: boolean;
}): ReactElement {
  const busy = loading || headline === null;
  return (
    <PiStatGrid>
      <PiStat label="Events" value={piCount(headline?.events)} loading={busy} />
      <PiStat label="Registrations" value={piCount(headline?.registrations)} loading={busy} />
      <PiStat
        label="Attendees"
        value={piCount(headline?.attendees)}
        detail={
          headline !== null && headline.attendanceRate !== null
            ? `${piPct(headline.attendanceRate)} attendance`
            : undefined
        }
        loading={busy}
      />
      <PiStat label="Booth connections" value={piCount(headline?.boothConnections)} loading={busy} />
    </PiStatGrid>
  );
}

const FORMAT_OPTIONS: readonly { value: EventsFormatFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'in_person', label: FORMAT_LABEL.in_person },
  { value: 'virtual', label: FORMAT_LABEL.virtual },
];

function positiveId(raw: string): number | null {
  const id = Number.parseInt(raw, 10);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

export function EventsFiltersBar({
  state,
  cycles,
  selectedCycleId,
  resolvedCycleId,
  regions,
  products,
  search,
  searchLabel,
  onSearchChange,
  onChange,
  trailing,
}: {
  state: EventsUrlState;
  /** null while the SEED FAB filters are loading. */
  cycles: readonly FabCycle[] | null;
  selectedCycleId: number | null;
  resolvedCycleId: number | null;
  regions: readonly FabRegion[];
  products: readonly ProductOption[];
  /** The typed (undebounced) search text. */
  search: string;
  searchLabel: string;
  onSearchChange(next: string): void;
  onChange(next: EventsUrlState): void;
  trailing?: ReactNode;
}): ReactElement {
  const regionNameOf = (id: number) => regions.find((r) => r.id === id)?.name;
  const productNameOf = (id: number) => products.find((p) => p.id === id)?.label;
  const regionOptions: MultiComboboxOption[] = [
    ...state.regionIds
      .filter((id) => regionNameOf(id) === undefined)
      .map((id) => ({ value: String(id), label: `Region ${id}` })),
    ...regions.map((region) => ({ value: String(region.id), label: region.name })),
  ];
  const productOptions: MultiComboboxOption[] = [
    ...state.productIds
      .filter((id) => productNameOf(id) === undefined)
      .map((id) => ({ value: String(id), label: `Product ${id}` })),
    ...products.map((product) => ({ value: String(product.id), label: product.label })),
  ];
  const idsFrom = (values: string[]) =>
    values.map(positiveId).filter((id): id is number => id !== null);
  const cycleName = cycles?.find((c) => c.id === state.cycleId)?.name;

  const chips: { key: string; label: ReactNode; onRemove: () => void }[] = [];
  if (state.cycleId !== null) {
    chips.push({
      key: 'cycle',
      label: cycleName ?? `Cycle ${state.cycleId}`,
      onRemove: () => onChange({ ...state, cycleId: null }),
    });
  }
  if (state.format !== 'all') {
    chips.push({
      key: 'format',
      label: FORMAT_LABEL[state.format],
      onRemove: () => onChange({ ...state, format: 'all' }),
    });
  }
  for (const id of state.regionIds) {
    chips.push({
      key: `region-${id}`,
      label: regionNameOf(id) ?? `Region ${id}`,
      onRemove: () => onChange({ ...state, regionIds: state.regionIds.filter((r) => r !== id) }),
    });
  }
  for (const id of state.productIds) {
    chips.push({
      key: `product-${id}`,
      label: productNameOf(id) ?? `Product ${id}`,
      onRemove: () => onChange({ ...state, productIds: state.productIds.filter((p) => p !== id) }),
    });
  }
  if (state.search.trim() !== '') {
    chips.push({
      key: 'q',
      label: `“${state.search.trim()}”`,
      onRemove: () => {
        onSearchChange('');
        onChange({ ...state, search: '' });
      },
    });
  }

  return (
    <PiToolbar
      trailing={trailing}
      chips={
        chips.length > 0 ? (
          <PiFilterChips
            chips={chips}
            onReset={() => {
              onSearchChange('');
              onChange(resetEventsFilters(state));
            }}
          />
        ) : undefined
      }
    >
      <div className="w-44 max-sm:w-full">
        <Select
          aria-label="Cycle"
          value={selectedCycleId !== null ? String(selectedCycleId) : ''}
          onChange={(event) => {
            const id = positiveId(event.target.value);
            if (id === null) return;
            onChange({ ...state, cycleId: id === resolvedCycleId ? null : id });
          }}
          disabled={cycles === null}
        >
          {cycles === null && <option value="">Cycle</option>}
          {cycles?.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.isCurrent ? `${c.name} (current)` : c.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="max-sm:w-full">
        <SegmentedControl
          label="Format"
          value={state.format}
          options={FORMAT_OPTIONS}
          onChange={(next) => onChange({ ...state, format: next })}
        />
      </div>
      <div className="w-40 max-sm:w-full">
        <Field label="Region" hideLabel>
          {(field) => (
            <MultiCombobox
              id={field.id}
              values={state.regionIds.map(String)}
              onChange={(values) => onChange({ ...state, regionIds: idsFrom(values) })}
              options={regionOptions}
              placeholder="All regions"
              maxVisibleChips={0}
              summaryNoun={{ singular: 'region', plural: 'regions' }}
            />
          )}
        </Field>
      </div>
      <div className="w-48 max-sm:w-full">
        <Field label="Product" hideLabel>
          {(field) => (
            <MultiCombobox
              id={field.id}
              values={state.productIds.map(String)}
              onChange={(values) => onChange({ ...state, productIds: idsFrom(values) })}
              options={productOptions}
              placeholder="All products"
              maxVisibleChips={0}
              summaryNoun={{ singular: 'product', plural: 'products' }}
            />
          )}
        </Field>
      </div>
      <PiSearch
        value={search}
        onChange={onSearchChange}
        label={searchLabel}
        className="min-w-0 flex-1 sm:max-w-xs max-sm:w-full"
      />
    </PiToolbar>
  );
}
